import { DataLayerId } from './types';

// Single source of truth for "who produced this dataset?". Drives the
// layer-picker logo prefix in both `DetailLayerDropdown` (map control) and
// the left-panel `LayerPickerListbox`. Names mirror the canonical strings
// used in each layer's `AboutLayer` copy and in `InsightProvenanceFooter`
// — keep them in sync if upstream attribution changes.
//
// Some layers carry a `collaborator` — a partner platform that hosts or
// renders the data (e.g. AGB is rendered via Earthmover Flux; Canopy
// Height is rendered via the Earthscale tiler; Tree Species Diversity is
// a USFS BIGMAP product packaged by CTrees). The picker shows both marks
// side-by-side and the subtitle reads "CTrees · via Earthscale · ..." so
// the integration is visible without overstating attribution.

export type DataProviderId =
  | 'ctrees'
  | 'usfs'
  | 'umaine'
  | 'natureserve'
  | 'earthscale'
  | 'earthmover';

export interface DataProvider {
  id: DataProviderId;
  name: string;            // Display name shown in the picker subtitle
  logoPath: string;        // Public URL relative to /
  isOriginal: boolean;     // True for CTrees-built or CTrees-derived layers
  shortLabel: string;      // Tight label for tooltips ("Source: USFS")
  // Some providers ship a single dark-fill mark intended for light
  // backgrounds. The dashboard runs on dark `figma-card`, so for those
  // we apply a CSS `filter: invert(1)` at render time rather than
  // modifying the asset. Toggle off if/when the provider ships a
  // dark-background variant.
  needsInvert?: boolean;
}

export const DATA_PROVIDERS: Record<DataProviderId, DataProvider> = {
  ctrees: {
    id: 'ctrees',
    name: 'CTrees',
    logoPath: '/providers/ctrees.svg',
    isOriginal: true,
    shortLabel: 'CTrees',
  },
  usfs: {
    id: 'usfs',
    name: 'USDA Forest Service',
    logoPath: '/providers/usfs.svg',
    isOriginal: false,
    shortLabel: 'USFS',
  },
  umaine: {
    id: 'umaine',
    name: 'University of Maine',
    logoPath: '/providers/umaine.png',
    isOriginal: false,
    shortLabel: 'UMaine',
  },
  natureserve: {
    id: 'natureserve',
    name: 'NatureServe',
    logoPath: '/providers/natureserve.png',
    isOriginal: false,
    shortLabel: 'NatureServe',
  },
  earthscale: {
    id: 'earthscale',
    name: 'Earthscale',
    logoPath: '/providers/earthscale.svg',
    isOriginal: false,
    shortLabel: 'Earthscale',
    needsInvert: true,
  },
  earthmover: {
    id: 'earthmover',
    name: 'Earthmover',
    logoPath: '/providers/earthmover.svg',
    isOriginal: false,
    shortLabel: 'Earthmover',
  },
};

export interface LayerProviderConfig {
  // Whoever produced the canonical published artifact the dashboard
  // consumes — drives the headline mark + the lime "CTrees" cue in the
  // subtitle when the layer is an original.
  primary: DataProviderId;
  // Optional partner platform that hosts / renders the data. Shown as a
  // secondary mark next to the primary, with "via <name>" in the subtitle.
  collaborator?: DataProviderId;
}

// Per-layer attribution config. AGB, Canopy Height, and Tree Species
// Diversity carry a collaborator; the rest have a single primary.
export const LAYER_PROVIDERS: Record<DataLayerId, LayerProviderConfig> = {
  aboveground_biomass: { primary: 'ctrees', collaborator: 'earthmover' },
  soil_carbon: { primary: 'ctrees' },
  growth_curves: { primary: 'ctrees' },
  species_diversity: { primary: 'usfs', collaborator: 'ctrees' },
  stand_density: { primary: 'umaine' },
  forest_ownership: { primary: 'usfs' },
  fire_risk: { primary: 'usfs' },
  deforestation_alerts: { primary: 'ctrees' },
  canopy_height: { primary: 'ctrees', collaborator: 'earthscale' },
  imperiled_species: { primary: 'natureserve' },
  resilience_priority: { primary: 'ctrees' },
  tree_crown_segmentation: { primary: 'ctrees' },
};

export interface LayerProviders {
  primary: DataProvider;
  collaborator: DataProvider | null;
}

export const getProvidersForLayer = (layerId: DataLayerId): LayerProviders => {
  const config = LAYER_PROVIDERS[layerId];
  return {
    primary: DATA_PROVIDERS[config.primary],
    collaborator: config.collaborator ? DATA_PROVIDERS[config.collaborator] : null,
  };
};
